// Partial Credit Scoring Service
// Unified scoring system for multi-format questions with partial credit support

export interface PartialCreditResult {
  total_score: number;
  max_score: number;
  percentage: number;
  is_partial: boolean;
  breakdown: Record<string, number>;
  explanation: string;
}

export class PartialCreditService {
  /**
   * Calculate partial credit for MCQ Multiple questions
   * Awards credit based on correct selections minus incorrect selections
   */
  static calculateMCQMultipleScore(
    selectedAnswers: Array<string | number>,
    correctAnswers: Array<string | number>,
    totalOptions: number,
    allowNegativeScore: boolean = false
  ): PartialCreditResult {
    const correctSelections = selectedAnswers.filter(id => correctAnswers.includes(id));
    const incorrectSelections = selectedAnswers.filter(id => !correctAnswers.includes(id));
    const missedCorrectAnswers = correctAnswers.filter(id => !selectedAnswers.includes(id));
    
    // Base scoring: +1 for each correct, -0.5 for each incorrect
    const rawScore = correctSelections.length - (incorrectSelections.length * 0.5);
    
    // Ensure non-negative score unless explicitly allowed
    const finalScore = allowNegativeScore ? rawScore : Math.max(0, rawScore);
    const maxScore = correctAnswers.length;
    const percentage = Math.min(100, Math.max(0, (finalScore / maxScore) * 100));
    
    const breakdown = {
      correct_selections: correctSelections.length,
      incorrect_selections: incorrectSelections.length,
      missed_correct: missedCorrectAnswers.length,
      penalty_applied: incorrectSelections.length * 0.5
    };
    
    const isPartial = finalScore > 0 && finalScore < maxScore;
    
    let explanation = '';
    if (finalScore === maxScore) {
      explanation = 'Perfect score! All correct answers selected with no incorrect selections.';
    } else if (finalScore === 0) {
      explanation = 'No points awarded. Consider reviewing the correct answers.';
    } else {
      explanation = `Partial credit: ${correctSelections.length}/${correctAnswers.length} correct selections`;
      if (incorrectSelections.length > 0) {
        explanation += `, penalty for ${incorrectSelections.length} incorrect selections`;
      }
    }
    
    return {
      total_score: finalScore,
      max_score: maxScore,
      percentage,
      is_partial: isPartial,
      breakdown,
      explanation
    };
  }

  /**
   * Calculate partial credit for Fill in the Blank questions
   * Uses string similarity for partial matches
   */
  static calculateFillBlankScore(
    userAnswers: Record<string, string>,
    correctAnswers: Record<string, { answer: string; alternates?: string[]; points?: number }>,
    caseSensitive: boolean = false,
    partialThreshold: number = 70
  ): PartialCreditResult {
    let totalScore = 0;
    let maxScore = 0;
    const breakdown: Record<string, number> = {};
    
    for (const [blankId, correctData] of Object.entries(correctAnswers)) {
      const userAnswer = userAnswers[blankId] || '';
      const blankPoints = correctData.points || 1;
      maxScore += blankPoints;
      
      let blankScore = 0;
      
      // Check exact match first
      const userAnswerProcessed = caseSensitive ? userAnswer.trim() : userAnswer.trim().toLowerCase();
      const correctAnswerProcessed = caseSensitive ? correctData.answer.trim() : correctData.answer.trim().toLowerCase();
      
      if (userAnswerProcessed === correctAnswerProcessed) {
        blankScore = blankPoints;
      } else {
        // Check alternates
        const alternateMatches = correctData.alternates?.some(alt => 
          (caseSensitive ? alt.trim() : alt.trim().toLowerCase()) === userAnswerProcessed
        );
        
        if (alternateMatches) {
          blankScore = blankPoints;
        } else if (userAnswerProcessed.length > 0) {
          // Calculate similarity for partial credit
          const similarity = this.calculateStringSimilarity(userAnswerProcessed, correctAnswerProcessed);
          
          if (similarity >= partialThreshold) {
            blankScore = (similarity / 100) * blankPoints;
          }
          
          // Also check similarity with alternates
          if (correctData.alternates && blankScore === 0) {
            for (const alt of correctData.alternates) {
              const altSimilarity = this.calculateStringSimilarity(
                userAnswerProcessed, 
                caseSensitive ? alt.trim() : alt.trim().toLowerCase()
              );
              if (altSimilarity >= partialThreshold) {
                blankScore = Math.max(blankScore, (altSimilarity / 100) * blankPoints);
              }
            }
          }
        }
      }
      
      totalScore += blankScore;
      breakdown[blankId] = blankScore;
    }
    
    const percentage = maxScore > 0 ? (totalScore / maxScore) * 100 : 0;
    const isPartial = totalScore > 0 && totalScore < maxScore;
    
    const filledBlanks = Object.values(userAnswers).filter(ans => ans.trim()).length;
    const totalBlanks = Object.keys(correctAnswers).length;
    
    let explanation = '';
    if (totalScore === maxScore) {
      explanation = 'Perfect score! All blanks filled correctly.';
    } else if (totalScore === 0) {
      explanation = 'No points awarded. Try checking spelling and reviewing the correct answers.';
    } else {
      explanation = `Partial credit: ${filledBlanks}/${totalBlanks} blanks attempted with ${percentage.toFixed(1)}% accuracy.`;
    }
    
    return {
      total_score: totalScore,
      max_score: maxScore,
      percentage,
      is_partial: isPartial,
      breakdown,
      explanation
    };
  }

  /**
   * Calculate partial credit for Ordering questions
   * Awards points for correct adjacent pairs and overall sequence accuracy
   */
  static calculateOrderingScore(
    userOrder: number[],
    correctOrder: number[],
    strictOrdering: boolean = false,
    allowPartialCredit: boolean = true
  ): PartialCreditResult {
    if (!allowPartialCredit) {
      // All-or-nothing scoring
      const isExact = JSON.stringify(userOrder) === JSON.stringify(correctOrder);
      return {
        total_score: isExact ? 1 : 0,
        max_score: 1,
        percentage: isExact ? 100 : 0,
        is_partial: false,
        breakdown: { exact_match: isExact ? 1 : 0 },
        explanation: isExact ? 'Perfect order!' : 'Order is not completely correct.'
      };
    }
    
    if (strictOrdering) {
      // Strict ordering: award points for each item in correct position
      let correctPositions = 0;
      const breakdown: Record<string, number> = {};
      
      for (let i = 0; i < Math.min(userOrder.length, correctOrder.length); i++) {
        if (userOrder[i] === correctOrder[i]) {
          correctPositions++;
          breakdown[`position_${i + 1}`] = 1;
        } else {
          breakdown[`position_${i + 1}`] = 0;
        }
      }
      
      const maxScore = correctOrder.length;
      const percentage = (correctPositions / maxScore) * 100;
      
      return {
        total_score: correctPositions,
        max_score: maxScore,
        percentage,
        is_partial: correctPositions > 0 && correctPositions < maxScore,
        breakdown,
        explanation: `${correctPositions}/${maxScore} items in correct positions.`
      };
    } else {
      // Flexible ordering: use Longest Common Subsequence (LCS) for partial credit
      const lcsLength = this.calculateLCS(userOrder, correctOrder);
      const adjacentPairs = this.countCorrectAdjacentPairs(userOrder, correctOrder);
      
      // Scoring: 50% for LCS, 50% for adjacent pairs
      const lcsScore = (lcsLength / correctOrder.length) * 0.5;
      const adjacentScore = (adjacentPairs / Math.max(1, correctOrder.length - 1)) * 0.5;
      const totalScore = lcsScore + adjacentScore;
      
      const breakdown = {
        longest_common_subsequence: lcsLength,
        correct_adjacent_pairs: adjacentPairs,
        lcs_contribution: lcsScore,
        adjacent_contribution: adjacentScore
      };
      
      const percentage = totalScore * 100;
      const isPartial = totalScore > 0 && totalScore < 1;
      
      let explanation = '';
      if (totalScore === 1) {
        explanation = 'Perfect ordering!';
      } else if (totalScore === 0) {
        explanation = 'No partial credit earned. Review the correct sequence.';
      } else {
        explanation = `Partial credit: ${lcsLength}/${correctOrder.length} items in sequence, ${adjacentPairs} correct adjacent pairs.`;
      }
      
      return {
        total_score: totalScore,
        max_score: 1,
        percentage,
        is_partial: isPartial,
        breakdown,
        explanation
      };
    }
  }

  /**
   * Calculate string similarity using Levenshtein distance
   */
  private static calculateStringSimilarity(str1: string, str2: string): number {
    if (str1 === str2) return 100;
    if (str1.length === 0) return str2.length === 0 ? 100 : 0;
    if (str2.length === 0) return 0;

    const matrix: number[][] = [];
    
    // Initialize matrix
    for (let i = 0; i <= str2.length; i++) {
      matrix[i] = [i];
    }
    for (let j = 0; j <= str1.length; j++) {
      matrix[0][j] = j;
    }

    // Fill matrix
    for (let i = 1; i <= str2.length; i++) {
      for (let j = 1; j <= str1.length; j++) {
        if (str2[i - 1] === str1[j - 1]) {
          matrix[i][j] = matrix[i - 1][j - 1];
        } else {
          matrix[i][j] = Math.min(
            matrix[i - 1][j - 1] + 1, // substitution
            matrix[i][j - 1] + 1,     // insertion
            matrix[i - 1][j] + 1      // deletion
          );
        }
      }
    }

    const distance = matrix[str2.length][str1.length];
    const maxLength = Math.max(str1.length, str2.length);
    
    return Math.max(0, ((maxLength - distance) / maxLength) * 100);
  }

  /**
   * Calculate Longest Common Subsequence length
   */
  private static calculateLCS(arr1: number[], arr2: number[]): number {
    const m = arr1.length;
    const n = arr2.length;
    const dp: number[][] = Array(m + 1).fill(null).map(() => Array(n + 1).fill(0));

    for (let i = 1; i <= m; i++) {
      for (let j = 1; j <= n; j++) {
        if (arr1[i - 1] === arr2[j - 1]) {
          dp[i][j] = dp[i - 1][j - 1] + 1;
        } else {
          dp[i][j] = Math.max(dp[i - 1][j], dp[i][j - 1]);
        }
      }
    }

    return dp[m][n];
  }

  /**
   * Count correct adjacent pairs in ordering
   */
  private static countCorrectAdjacentPairs(userOrder: number[], correctOrder: number[]): number {
    const correctMap = new Map<number, number>();
    correctOrder.forEach((item, index) => correctMap.set(item, index));

    let correctPairs = 0;
    for (let i = 0; i < userOrder.length - 1; i++) {
      const currentItem = userOrder[i];
      const nextItem = userOrder[i + 1];
      
      const currentCorrectPos = correctMap.get(currentItem);
      const nextCorrectPos = correctMap.get(nextItem);
      
      if (currentCorrectPos !== undefined && nextCorrectPos !== undefined) {
        if (nextCorrectPos === currentCorrectPos + 1) {
          correctPairs++;
        }
      }
    }

    return correctPairs;
  }
}
